import type { ReactNode } from "react";

import { cn } from "@btnet/utilities-core";

import type { IShellNavItem } from "../BTShellLayout/BTShellLayout";
import { BTText } from "../BTText/BTText";

const NAV_GROUP_COUNT = 4;

interface ISidebarProps {
  navItems: IShellNavItem[];
  selectedKey: string;
  onSelectKey: (key: string) => void;
  /**
   * Top slot — reserved for the account/inbox/jobs block from Harriet's
   * prototype. Not built in this PR (ado-285671 scope); left empty until a
   * follow-up story fills it in.
   */
  top?: ReactNode;
  /**
   * Bottom slot — reserved for the research-preview opt-in/opt-out control
   * (e.g. `InfoArchOptInToggle`). BTVerticalNav has no opinion on what
   * opting in means or how it's persisted — the caller supplies the
   * fully-wired control as a plain ReactNode, matching `top`.
   */
  bottom?: ReactNode;
  className?: string;
  "data-testid": string;
}

function groupNavItems(navItems: IShellNavItem[]): IShellNavItem[][] {
  const groupSize = Math.ceil(navItems.length / NAV_GROUP_COUNT);
  return Array.from({ length: NAV_GROUP_COUNT }, (_, i) =>
    navItems.slice(i * groupSize, (i + 1) * groupSize),
  ).filter((group) => group.length > 0);
}

/**
 * Left nav for the research-preview shell (ado-285671). Composed of a `top`
 * slot (unbuilt placeholder for now), the shared `navItems` split into
 * `NAV_GROUP_COUNT` labeled groups (matching Harriet's prototype's
 * `shell-nav-list` / `shell-nav-group` / `shell-nav-item` classes, reproduced
 * here with Tailwind utilities on BDS tokens), and a `bottom` slot for a
 * caller-supplied control such as the research-preview opt-in toggle.
 */
export function BTVerticalNav({
  navItems,
  selectedKey,
  onSelectKey,
  top,
  bottom,
  className,
  "data-testid": testid,
}: ISidebarProps) {
  const groups = groupNavItems(navItems);

  return (
    <nav
      data-testid={testid}
      aria-label="Primary"
      className={cn(
        "flex w-60 shrink-0 flex-col overflow-y-auto border-r border-gray-15 bg-white",
        className,
      )}
    >
      {top}
      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-2">
        {groups.map((group, groupIndex) => (
          <div key={groupIndex} className="flex flex-col gap-px">
            <BTText
              testid={`${testid}-group-${groupIndex}-label`}
              size="normal-sm"
              tag="div"
              className="px-3 pb-1 pt-3 font-semibold text-fg-default first:pt-0"
            >
              {`Group ${groupIndex + 1}`}
            </BTText>
            {group.map((item) => {
              const isSelected = item.key === selectedKey;
              return (
                <div
                  key={item.key}
                  role="button"
                  tabIndex={0}
                  data-testid={`${testid}-item-${item.key}`}
                  onClick={() => onSelectKey(item.key)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelectKey(item.key);
                    }
                  }}
                  className={cn(
                    "cursor-pointer rounded-sm px-3 py-1.5 text-sm hover:bg-gray-5",
                    {
                      "bg-gray-10 font-medium text-gray-90 hover:bg-gray-10":
                        isSelected,
                      "font-normal text-gray-90": !isSelected,
                    },
                  )}
                >
                  {item.label}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      {bottom && (
        <div className="flex items-center gap-2 border-t border-gray-15 px-3 py-2">
          {bottom}
        </div>
      )}
    </nav>
  );
}
